import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
    Plus, FileText, CheckCircle2, XCircle, Eye, Trash2, Send, Lock, Download
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import { Checkbox } from '../../components/ui/checkbox';
import { generateVoucherPDF } from '../../utils/pdfExport';
import ReportWorkflowTrail from '../reports/ReportWorkflowTrail';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAtcSummary = (record, prefix) => {
    const codes = record?.[`${prefix}_atc_codes`];
    if (Array.isArray(codes) && codes.filter(Boolean).length) {
        return codes.filter(Boolean).join(', ');
    }
    return [1, 2, 3, 4]
        .map(i => record?.[`${prefix}_atc_${i}`])
        .filter(Boolean)
        .join(', ');
};

const getMasterAtcSummary = (record) => {
    const codes = record?.atc_codes;
    if (Array.isArray(codes) && codes.filter(Boolean).length) {
        return codes.filter(Boolean).join(', ');
    }
    return [record?.atc_1, record?.atc_2, record?.atc_3, record?.atc_4]
        .filter(Boolean)
        .join(', ');
};

const calculateWithholdingAmount = (enabled, taxBasis, rate) => {
    if (!enabled) return 0;
    const basisValue = parseFloat(taxBasis) || 0;
    const rateValue = parseFloat(rate) || 0;
    return Math.round((basisValue * (rateValue / 100)) * 100) / 100;
};

const hasVoucherWithholding = (voucher) => (
    !!voucher?.withholding_enabled || (parseFloat(voucher?.withholding_amount) || 0) > 0
);

export default function APVouchersTab({
    suppliers = [],
    accounts = [],
    fmt,
    onRefresh,
    selectedCourse,
    permissions,
    viewingStudentId,
    viewingStudentName,
    isReadOnly = false,
    sourceOpenRequest = null
}) {
    // Default permissions (student-level) if not provided
    const basePerms = permissions || {
        can_create: true,
        can_edit_draft: true,
        can_post: false,
        can_cancel: false,
        can_unpost: false,
        can_view_reports: true
    };
    const perms = isReadOnly
        ? { ...basePerms, can_create: false, can_edit_draft: false, can_post: false, can_cancel: false, can_unpost: false }
        : basePerms;
    const canPostInCurrentScope = !isReadOnly && perms.can_post;
    const canReverseInCurrentScope = basePerms.can_unpost && (!isReadOnly || !!viewingStudentId);
    const draftReviewer = canPostInCurrentScope
        ? 'You can review and post it after checking supplier, line accounts, WTAX, and support.'
        : 'Submit it for instructor/admin review when ready. Submitted vouchers appear in Review > AR/AP Review Queue.';
    const draftNextAction = canPostInCurrentScope
        ? 'Open the voucher row to review supplier, lines, WTAX, and support before posting.'
        : 'Click Submit for Review so the instructor can approve/post it or return it with feedback.';
    
    const [vouchers, setVouchers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [selectedVoucher, setSelectedVoucher] = useState(null);
    const [postTarget, setPostTarget] = useState(null);
    const [posting, setPosting] = useState(false);
    const [showReversalDialog, setShowReversalDialog] = useState(false);
    const [reversalTarget, setReversalTarget] = useState(null);
    const [reversalReason, setReversalReason] = useState('');
    const [reversing, setReversing] = useState(false);
    const [lastResult, setLastResult] = useState(null);
    const lastSourceOpenRequestId = useRef(null);
    const [formData, setFormData] = useState({
        supplier_id: '',
        voucher_date: new Date().toISOString().split('T')[0],
        due_date: '',
        terms: '',
        particulars: '',
        reference_number: '',
        lines: [{ account_code: '', account_name: '', description: '', amount: 0 }],
        total_amount: 0,
        withholding_enabled: false,
        withholding_tax_basis: 0,
        withholding_rate: 0,
        withholding_amount: 0,
        withholding_atc: '',
        withholding_account: 'Expanded Withholding Tax Payable'
    });

    const fetchVouchers = useCallback(async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const params = new URLSearchParams();
            if (viewingStudentId) params.append('student_id', viewingStudentId);
            const response = await axios.get(`${API}/ap/vouchers${params.toString() ? `?${params.toString()}` : ''}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setVouchers(response.data);
        } catch (error) {
            toast.error('Failed to load AP vouchers');
        } finally {
            setLoading(false);
        }
    }, [viewingStudentId]);

    useEffect(() => {
        fetchVouchers();
    }, [fetchVouchers]);

    useEffect(() => {
        if (!sourceOpenRequest?.id || !sourceOpenRequest.sourceType?.startsWith('ap_voucher') || loading) return;
        if (lastSourceOpenRequestId.current === sourceOpenRequest.id) return;

        const requestedId = String(sourceOpenRequest.sourceId || '').trim();
        const requestedRef = String(sourceOpenRequest.refNo || '').trim().toLowerCase();
        const matchingVoucher = vouchers.find((voucher) => {
            const voucherId = String(voucher.id || '').trim();
            const voucherNumber = String(voucher.voucher_number || '').trim();
            const voucherNumberLower = voucherNumber.toLowerCase();
            return (requestedId && voucherId === requestedId)
                || (requestedId && voucherNumber === requestedId)
                || (requestedRef && voucherNumberLower === requestedRef)
                || (requestedRef && requestedRef === `rev-${voucherNumberLower}`);
        });

        if (!matchingVoucher && vouchers.length === 0) return;

        lastSourceOpenRequestId.current = sourceOpenRequest.id;
        if (matchingVoucher) {
            setSelectedVoucher(matchingVoucher);
            toast.success(`Opened ${sourceOpenRequest.label || 'AP Voucher'} ${matchingVoucher.voucher_number} from ${sourceOpenRequest.sourceOrigin || 'the source trail'}.`);
        } else {
            toast.info(`The AP Voucher source ${sourceOpenRequest.refNo || sourceOpenRequest.sourceId || ''} is not visible in this current course/student scope.`);
        }
    }, [sourceOpenRequest, vouchers, loading]);

    const handleAddLine = () => {
        setFormData(prev => ({
            ...prev,
            lines: [...prev.lines, { account_code: '', account_name: '', description: '', amount: 0 }]
        }));
    };

    const handleRemoveLine = (index) => {
        if (formData.lines.length > 1) {
            const newLines = formData.lines.filter((_, i) => i !== index);
            const total = newLines.reduce((sum, line) => sum + (parseFloat(line.amount) || 0), 0);
            setFormData(prev => {
                const syncBasis = !prev.withholding_tax_basis || Number(prev.withholding_tax_basis) === Number(prev.total_amount);
                const taxBasis = syncBasis ? total : prev.withholding_tax_basis;
                return {
                    ...prev,
                    lines: newLines,
                    total_amount: total,
                    withholding_tax_basis: taxBasis,
                    withholding_amount: calculateWithholdingAmount(prev.withholding_enabled, taxBasis, prev.withholding_rate)
                };
            });
        }
    };

    const handleLineChange = (index, field, value) => {
        const newLines = [...formData.lines];
        newLines[index][field] = value;
        
        // If account selected, set account_name
        if (field === 'account_code') {
            const account = accounts.find(a => a.account_code === value);
            if (account) {
                newLines[index].account_name = account.account_name;
            }
        }
        
        const total = newLines.reduce((sum, line) => sum + (parseFloat(line.amount) || 0), 0);
        setFormData(prev => {
            const syncBasis = !prev.withholding_tax_basis || Number(prev.withholding_tax_basis) === Number(prev.total_amount);
            const taxBasis = syncBasis ? total : prev.withholding_tax_basis;
            return {
                ...prev,
                lines: newLines,
                total_amount: total,
                withholding_tax_basis: taxBasis,
                withholding_amount: calculateWithholdingAmount(prev.withholding_enabled, taxBasis, prev.withholding_rate)
            };
        });
    };

    const updateWithholding = (changes) => {
        setFormData(prev => {
            const enabled = Object.prototype.hasOwnProperty.call(changes, 'withholding_enabled')
                ? changes.withholding_enabled
                : prev.withholding_enabled;
            const taxBasis = Object.prototype.hasOwnProperty.call(changes, 'withholding_tax_basis')
                ? changes.withholding_tax_basis
                : (prev.withholding_tax_basis || prev.total_amount);
            const rate = Object.prototype.hasOwnProperty.call(changes, 'withholding_rate')
                ? changes.withholding_rate
                : prev.withholding_rate;
            return {
                ...prev,
                ...changes,
                withholding_enabled: enabled,
                withholding_tax_basis: enabled ? taxBasis : 0,
                withholding_rate: enabled ? rate : 0,
                withholding_amount: calculateWithholdingAmount(enabled, taxBasis, rate)
            };
        });
    };

    const handleSubmit = async () => {
        if (!formData.supplier_id || !formData.particulars || formData.total_amount <= 0) {
            toast.error('Please fill all required fields');
            return;
        }

        try {
            const token = localStorage.getItem('token');
            const response = await axios.post(`${API}/ap/vouchers`, {
                ...formData,
                course_id: selectedCourse || null,
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const voucherNumber = response.data?.voucher_number || 'New voucher';
            setLastResult({
                title: `AP Voucher ${voucherNumber} saved`,
                status: 'Draft',
                amount: formData.total_amount,
                source: selectedSupplier?.supplier_name || formData.supplier_id,
                reference: formData.reference_number || 'Supplier bill/support',
                location: 'Record > AP Voucher list. Submit it next to send it to Review > AR/AP Review Queue.',
                reviewer: draftReviewer,
                glImpact: 'Draft only. It does not affect GL, Trial Balance, AP balances, or WTAX reports until posted.',
                nextAction: draftNextAction
            });
            toast.success(
                `AP Voucher ${response.data?.voucher_number || ''} saved as Draft. Find it in Record > AP Voucher; it is not in GL/Trial Balance yet. Next: click Submit for Review so the instructor can approve/post or return it.`
            );
            setShowForm(false);
            resetForm();
            fetchVouchers();
            if (onRefresh) onRefresh();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to create voucher');
        }
    };

    const handleSubmitForReview = async (voucher) => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.put(`${API}/ap/vouchers/${voucher.voucher_number}/submit`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const updated = response.data?.voucher || voucher;
            setLastResult({
                title: `AP Voucher ${updated.voucher_number || voucher.voucher_number} submitted`,
                status: 'Submitted for Review',
                amount: updated.total_amount || voucher.total_amount,
                source: updated.supplier_name || voucher.supplier_name || 'Supplier voucher',
                reference: updated.reference_number || getAtcSummary(updated, 'supplier') || 'AP voucher review',
                location: response.data?.next_location || 'Review > AR/AP Review Queue',
                reviewer: 'Instructor/admin reviews next and can approve/post or return with feedback.',
                glImpact: response.data?.gl_impact || 'Submitted only. No GL or Trial Balance impact until posted.',
                nextAction: 'Wait for instructor review, or monitor this voucher status in Record > AP Voucher.'
            });
            toast.success(`AP Voucher ${updated.voucher_number || voucher.voucher_number} submitted for instructor/admin review. Find it now in Review > AR/AP Review Queue and in your AP Voucher list as Submitted for Review; GL/Trial Balance changes only after posting.`);
            fetchVouchers();
            if (onRefresh) onRefresh();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to submit voucher for review');
        }
    };

    const openPostDialog = (voucher) => {
        setPostTarget(voucher);
    };

    const closePostDialog = () => {
        if (posting) return;
        setPostTarget(null);
    };

    const handlePost = async () => {
        if (!postTarget?.voucher_number) return;
        setPosting(true);
        try {
            const token = localStorage.getItem('token');
            await axios.put(`${API}/ap/vouchers/${postTarget.voucher_number}/post`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setLastResult({
                title: `AP Voucher ${postTarget.voucher_number} posted`,
                status: 'Posted',
                amount: postTarget.total_amount,
                source: postTarget.supplier_name || 'Supplier voucher',
                reference: postTarget.reference_number || getAtcSummary(postTarget, 'supplier') || 'AP voucher posting',
                location: 'Record > AP Voucher, General Ledger, Trial Balance, AP reports, and AP WTAX when applicable',
                reviewer: 'Instructor/admin has posted it; auditors can review the source voucher and GL evidence.',
                glImpact: 'Posted voucher now affects GL, Trial Balance, AP balances, and withholding reports when WTAX is enabled.',
                nextAction: 'Verify the GL lines and AP balance. Use reversal only if the posted voucher needs correction.'
            });
            toast.success(
                `AP Voucher ${postTarget.voucher_number} posted. It now affects GL and Trial Balance; instructors can find it in Dashboard > Recent Student Accounting Activity and Review > AP Vouchers.`
            );
            setPostTarget(null);
            fetchVouchers();
            if (onRefresh) onRefresh();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to post voucher');
        } finally {
            setPosting(false);
        }
    };

    const handleCancel = async (voucherNumber) => {
        try {
            const token = localStorage.getItem('token');
            const target = vouchers.find(v => v.voucher_number === voucherNumber);
            await axios.put(`${API}/ap/vouchers/${voucherNumber}/cancel`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setLastResult({
                title: `AP Voucher ${voucherNumber} cancelled`,
                status: 'Cancelled',
                amount: target?.total_amount,
                source: target?.supplier_name || 'Supplier voucher',
                reference: target?.reference_number || 'Draft cancellation',
                location: 'Record > AP Voucher list with Cancelled status',
                reviewer: 'Instructor/admin cancelled the draft before posting.',
                glImpact: 'Cancelled draft does not affect GL, Trial Balance, AP balances, or WTAX reports.',
                nextAction: 'Create a new voucher only if the supplier bill still needs to be recorded.'
            });
            toast.success(
                `AP Voucher ${voucherNumber} cancelled. It remains in Record > AP Voucher with Cancelled status and does not affect GL or Trial Balance.`
            );
            fetchVouchers();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to cancel voucher');
        }
    };

    const openReversalDialog = (voucher) => {
        setReversalTarget(voucher);
        setReversalReason('');
        setShowReversalDialog(true);
    };

    const handleReverse = async () => {
        if (!reversalReason.trim()) {
            toast.error('Please provide a reason for reversal');
            return;
        }
        
        setReversing(true);
        try {
            const token = localStorage.getItem('token');
            const params = new URLSearchParams();
            if (viewingStudentId) params.append('student_id', viewingStudentId);
            const response = await axios.put(
                `${API}/ap/vouchers/${reversalTarget.voucher_number}/reverse${params.toString() ? `?${params.toString()}` : ''}`,
                { reason: reversalReason },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setLastResult({
                title: `AP Voucher ${reversalTarget.voucher_number} reversed`,
                status: 'Reversed',
                amount: reversalTarget.total_amount,
                source: reversalTarget.supplier_name || 'Supplier voucher',
                reference: response.data.reversal_ref || reversalTarget.reversal_ref || 'Reversal entry',
                location: 'Record > AP Voucher, General Ledger, and reversal evidence',
                reviewer: 'Instructor/admin completed the reversal; auditors can review the reason and reference.',
                glImpact: 'Reversal creates offsetting GL entries and removes the posted voucher effect from AP balances and reports.',
                nextAction: 'Review the reversal reference, then create a corrected voucher only if the supplier bill still needs posting.'
            });
            toast.success(
                `AP Voucher ${reversalTarget.voucher_number} reversed. Reference: ${response.data.reversal_ref}. Review it in Record > AP Voucher and General Ledger.`
            );
            setShowReversalDialog(false);
            setReversalTarget(null);
            setReversalReason('');
            fetchVouchers();
            if (onRefresh) onRefresh();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to reverse voucher');
        } finally {
            setReversing(false);
        }
    };

    const resetForm = () => {
        setFormData({
            supplier_id: '',
            voucher_date: new Date().toISOString().split('T')[0],
            due_date: '',
            terms: '',
            particulars: '',
            reference_number: '',
            lines: [{ account_code: '', account_name: '', description: '', amount: 0 }],
            total_amount: 0,
            withholding_enabled: false,
            withholding_tax_basis: 0,
            withholding_rate: 0,
            withholding_amount: 0,
            withholding_atc: '',
            withholding_account: 'Expanded Withholding Tax Payable'
        });
        setSelectedVoucher(null);
    };

    const getDisplayStatus = (record) => {
        if (record.review_status === 'returned') return 'returned';
        if (record.status === 'submitted') return 'submitted';
        return record.status || 'draft';
    };

    const getStatusBadge = (record) => {
        const status = getDisplayStatus(record);
        const styles = {
            draft: 'bg-amber-100 text-amber-700',
            submitted: 'bg-blue-100 text-blue-700',
            returned: 'bg-orange-100 text-orange-700',
            posted: 'bg-emerald-100 text-emerald-700',
            cancelled: 'bg-red-100 text-red-700',
            reversed: 'bg-purple-100 text-purple-700'
        };
        const labels = {
            draft: 'Draft',
            submitted: 'Submitted for Review',
            returned: 'Returned for Revision',
            posted: 'Posted',
            cancelled: 'Cancelled',
            reversed: 'Reversed'
        };
        return <span className={`px-2 py-0.5 rounded text-xs font-medium ${styles[status] || styles.draft}`}>{labels[status] || status}</span>;
    };

    const selectedSupplier = suppliers.find(s => s.supplier_id === formData.supplier_id || s.id === formData.supplier_id);
    const selectedSupplierAtc = getMasterAtcSummary(selectedSupplier);
    const netPayablePreview = Math.max(0, (parseFloat(formData.total_amount) || 0) - (parseFloat(formData.withholding_amount) || 0));
    const workflowScope = viewingStudentName
        ? `Reviewing ${viewingStudentName}`
        : isReadOnly
            ? 'Read-only review'
            : 'Your workspace';
    const workflowItems = [
        {
            label: 'Source work',
            value: 'AP vouchers record supplier bills, expense lines, payables, and optional withholding tax support.'
        },
        {
            label: 'Where it goes',
            value: 'Saved vouchers stay in Record > AP Voucher and appear in instructor activity for review.'
        },
        {
            label: 'Approval',
            value: canPostInCurrentScope
                ? 'Instructors/admins post, cancel drafts, or reverse posted vouchers after checking support.'
                : 'Students submit AP vouchers for instructor/admin review. Submitted work appears in Review > AR/AP Review Queue.'
        },
        {
            label: 'GL and TB',
            value: 'Only posted vouchers and reversal entries affect GL, Trial Balance, AP balances, and WTAX reports.'
        },
        {
            label: 'Next safe action',
            value: canPostInCurrentScope
                ? 'Check supplier, line accounts, WTAX, and reference before posting. Use reversal for posted corrections.'
                : 'Save the voucher, then submit it for review. It affects GL/Trial Balance only after instructor posting.'
        }
    ];

    const getVoucherDetailTrail = (voucher) => {
        const status = getDisplayStatus(voucher);
        const reversalRef = voucher.reversal_ref || voucher.reversal_reference || voucher.reversal_number;
        if (status === 'posted') {
            return {
                location: 'Record > AP Voucher, General Ledger, Trial Balance, AP Summary/Aging, and WTAX reports when applicable.',
                reviewer: 'No reviewer is waiting. Instructor/admin already posted this voucher; auditors can review it read-only.',
                impact: 'Affects GL, Trial Balance, AP balances, and withholding reports when WTAX is enabled.',
                nextAction: 'Verify the GL lines and AP balance. Use reversal only if the posted supplier bill needs correction.'
            };
        }
        if (status === 'reversed') {
            return {
                location: `Record > AP Voucher and General Ledger${reversalRef ? ` via reversal reference ${reversalRef}` : ''}.`,
                reviewer: 'No reviewer is waiting. The reversal is audit evidence for the original posted voucher.',
                impact: 'Reversal entries offset the original AP voucher impact in GL, Trial Balance, AP balances, and reports.',
                nextAction: 'Review the reversal reason and reference. Create a corrected voucher only if the supplier bill still needs posting.'
            };
        }
        if (status === 'submitted') {
            return {
                location: 'Review > AR/AP Review Queue and Record > AP Voucher list.',
                reviewer: 'Instructor/admin reviews next and can post it or return it with feedback.',
                impact: 'Submitted only. It does not affect GL, Trial Balance, AP balances, or WTAX reports until posted.',
                nextAction: 'Wait for review or monitor this voucher status in the AP Voucher list.'
            };
        }
        if (status === 'returned') {
            return {
                location: 'Record > AP Voucher list as Returned for Revision.',
                reviewer: 'Student revises next, then resubmits for instructor/admin review.',
                impact: 'Returned work is evidence only. It does not affect GL, Trial Balance, AP balances, or reports.',
                nextAction: 'Read the feedback, revise supplier/line/WTAX details, and resubmit only when corrected.'
            };
        }
        if (status === 'cancelled') {
            return {
                location: 'Record > AP Voucher list with Cancelled status.',
                reviewer: 'No reviewer is waiting because the draft was cancelled before posting.',
                impact: 'Cancelled drafts do not affect GL, Trial Balance, AP balances, or WTAX reports.',
                nextAction: 'Create a new voucher only if the supplier bill still needs to be recorded.'
            };
        }
        return {
            location: 'Record > AP Voucher list.',
            reviewer: draftReviewer,
            impact: 'Draft only. It does not affect GL, Trial Balance, AP balances, or WTAX reports until posted.',
            nextAction: draftNextAction
        };
    };

    return (
        <div className="space-y-6" data-testid="ap-vouchers-tab">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-xl font-bold text-slate-900 font-['Manrope']">AP Vouchers</h2>
                    <p className="text-sm text-slate-500">
                        {viewingStudentName ? `Review supplier vouchers for ${viewingStudentName}` : 'Manage supplier bills and payables'}
                    </p>
                </div>
                {!isReadOnly && (
                    <Button onClick={() => setShowForm(true)} className="bg-[#2F8FCB]" data-testid="new-voucher-btn">
                        <Plus className="w-4 h-4 mr-2" /> New Voucher
                    </Button>
                )}
            </div>

            {isReadOnly && (
                <div className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-900" data-testid="ap-vouchers-readonly-scope">
                    Student review mode is read-only. Use this list to verify AP vouchers the student created or posted, then check General Ledger or Trial Balance for posted accounting effect.
                </div>
            )}

            <ReportWorkflowTrail
                title="AP Vouchers trail"
                scope={workflowScope}
                items={workflowItems}
                testId="ap-vouchers-workflow-trail"
            />

            {lastResult && (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-950" data-testid="ap-voucher-result-trail">
                    <div className="flex flex-col gap-1 md:flex-row md:items-start md:justify-between">
                        <div>
                            <p className="font-semibold">{lastResult.title}</p>
                            <p className="mt-1 text-emerald-800">
                                Status: <span className="font-semibold">{lastResult.status}</span>
                                {lastResult.amount !== undefined && lastResult.amount !== null ? ` | Amount: ${fmt(lastResult.amount)}` : ''}
                            </p>
                        </div>
                        <span className="w-fit rounded-full bg-white px-3 py-1 text-xs font-semibold text-emerald-700">
                            Latest action
                        </span>
                    </div>
                    <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-3">
                        <p><span className="font-semibold">Created/changed:</span> {lastResult.source}</p>
                        <p><span className="font-semibold">Reference:</span> {lastResult.reference}</p>
                        <p><span className="font-semibold">Find it now:</span> {lastResult.location}</p>
                        <p><span className="font-semibold">Review:</span> {lastResult.reviewer}</p>
                        <p><span className="font-semibold">Accounting impact:</span> {lastResult.glImpact}</p>
                        <p><span className="font-semibold">Next safe action:</span> {lastResult.nextAction}</p>
                    </div>
                </div>
            )}

            {/* Voucher List */}
            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                <table className="w-full min-w-[920px] text-sm">
                    <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
                        <tr>
                            <th className="text-left px-4 py-3">Voucher #</th>
                            <th className="text-left px-4 py-3">Supplier</th>
                            <th className="text-left px-4 py-3">Date</th>
                            <th className="text-left px-4 py-3">Reference</th>
                            <th className="text-right px-4 py-3">Amount</th>
                            <th className="text-right px-4 py-3">Balance</th>
                            <th className="text-center px-4 py-3">Status</th>
                            <th className="text-center px-4 py-3">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {loading ? (
                            <tr><td colSpan="8" className="px-4 py-8 text-center text-slate-400">Loading...</td></tr>
                        ) : vouchers.length === 0 ? (
                            <tr><td colSpan="8" className="px-4 py-8 text-center text-slate-400">No AP vouchers found</td></tr>
                        ) : (
                            vouchers.map((v) => (
                                <tr key={v.id} className="hover:bg-slate-50">
                                    <td className="px-4 py-3 font-mono text-sm">{v.voucher_number}</td>
                                    <td className="px-4 py-3">
                                        <p className="font-medium">{v.supplier_name}</p>
                                        <p className="text-xs text-slate-400">{v.supplier_id}</p>
                                        {v.supplier_tin && (
                                            <p className="text-xs text-slate-500">TIN {v.supplier_tin}</p>
                                        )}
                                        {getAtcSummary(v, 'supplier') && (
                                            <p className="text-xs text-slate-500">ATC {getAtcSummary(v, 'supplier')}</p>
                                        )}
                                    </td>
                                    <td className="px-4 py-3">{v.voucher_date}</td>
                                    <td className="px-4 py-3 text-slate-500">{v.reference_number || '-'}</td>
                                    <td className="px-4 py-3 text-right font-mono">
                                        <p>{fmt(v.total_amount)}</p>
                                        {v.withholding_enabled && (
                                            <p className="text-xs text-slate-500">WTAX {fmt(v.withholding_amount || 0)}</p>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-right font-mono text-amber-600">{fmt(v.balance)}</td>
                                    <td className="px-4 py-3 text-center">
                                        {getStatusBadge(v)}
                                        {v.review_status === 'returned' && (
                                            <div className="mx-auto mt-2 max-w-[240px] rounded-md border border-orange-200 bg-orange-50 px-2 py-1 text-left text-xs text-orange-800" data-testid={`returned-ap-voucher-row-note-${v.voucher_number}`}>
                                                <p className="font-semibold">Returned: revise this voucher, then resubmit.</p>
                                                <p className="mt-0.5 line-clamp-2">Feedback: {v.return_reason || 'Open the voucher details to read instructor feedback.'}</p>
                                                <p className="mt-0.5">No GL, Trial Balance, or AP report impact until posted.</p>
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-center">
                                        <TooltipProvider>
                                        <div className="flex items-center justify-center gap-1">
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => setSelectedVoucher(v)}
                                                data-testid={`view-voucher-${v.voucher_number}`}
                                                aria-label={`View voucher ${v.voucher_number} source details`}
                                                title={`View voucher ${v.voucher_number} source details`}
                                            >
                                                <Eye className="w-4 h-4" />
                                            </Button>
                                            {v.status === 'draft' && (
                                                <>
                                                    {perms.can_post ? (
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            className="text-emerald-600"
                                                            onClick={() => openPostDialog(v)}
                                                            data-testid={`post-voucher-${v.voucher_number}`}
                                                            aria-label={`Post voucher ${v.voucher_number}`}
                                                            title={`Post voucher ${v.voucher_number}`}
                                                        >
                                                            <Send className="w-4 h-4" />
                                                        </Button>
                                                    ) : (
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            className="text-blue-600"
                                                            onClick={() => handleSubmitForReview(v)}
                                                            data-testid={`submit-voucher-${v.voucher_number}`}
                                                            aria-label={`Submit voucher ${v.voucher_number} for review`}
                                                            title={`Submit voucher ${v.voucher_number} for review`}
                                                        >
                                                            <Send className="w-4 h-4" />
                                                        </Button>
                                                    )}
                                                    {perms.can_cancel ? (
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            className="text-red-600"
                                                            onClick={() => handleCancel(v.voucher_number)}
                                                            data-testid={`cancel-voucher-${v.voucher_number}`}
                                                            aria-label={`Cancel voucher ${v.voucher_number}`}
                                                            title={`Cancel voucher ${v.voucher_number}`}
                                                        >
                                                            <XCircle className="w-4 h-4" />
                                                        </Button>
                                                    ) : (
                                                        <Tooltip>
                                                            <TooltipTrigger asChild>
                                                                <Button
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    className="text-slate-300 cursor-not-allowed"
                                                                    disabled
                                                                    aria-label={`Cancel voucher ${v.voucher_number} unavailable`}
                                                                    title={`Cancel voucher ${v.voucher_number} unavailable`}
                                                                >
                                                                    <XCircle className="w-4 h-4" />
                                                                </Button>
                                                            </TooltipTrigger>
                                                            <TooltipContent>
                                                                <p>Only instructors can cancel vouchers</p>
                                                            </TooltipContent>
                                                        </Tooltip>
                                                    )}
                                                </>
                                            )}
                                            {v.status === 'submitted' && !isReadOnly && (
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            className="text-blue-500 cursor-not-allowed"
                                                            disabled
                                                            aria-label={`Voucher ${v.voucher_number} is submitted to AR/AP Review Queue`}
                                                            title={`Voucher ${v.voucher_number} is submitted to AR/AP Review Queue`}
                                                        >
                                                            <Lock className="w-4 h-4" />
                                                        </Button>
                                                    </TooltipTrigger>
                                                    <TooltipContent><p>Submitted to Review &gt; AR/AP Review Queue</p></TooltipContent>
                                                </Tooltip>
                                            )}
                                            {v.status === 'posted' && (
                                                <>
                                                    {canReverseInCurrentScope ? (
                                                        <Tooltip>
                                                            <TooltipTrigger asChild>
                                                                <Button
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    className="text-purple-600"
                                                                    onClick={() => openReversalDialog(v)}
                                                                    data-testid={`reverse-voucher-${v.voucher_number}`}
                                                                    aria-label={`Reverse voucher ${v.voucher_number}`}
                                                                    title={`Reverse voucher ${v.voucher_number}`}
                                                                >
                                                                    <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                        <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/>
                                                                        <path d="M3 3v5h5"/>
                                                                    </svg>
                                                                </Button>
                                                            </TooltipTrigger>
                                                            <TooltipContent>
                                                                <p>Reverse this posted voucher with a required reason</p>
                                                            </TooltipContent>
                                                        </Tooltip>
                                                    ) : (
                                                        <Tooltip>
                                                            <TooltipTrigger asChild>
                                                                <Button
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    className="text-slate-300 cursor-not-allowed"
                                                                    disabled
                                                                    aria-label={`Reverse voucher ${v.voucher_number} unavailable`}
                                                                    title={`Reverse voucher ${v.voucher_number} unavailable`}
                                                                >
                                                                    <Lock className="w-4 h-4" />
                                                                </Button>
                                                            </TooltipTrigger>
                                                            <TooltipContent>
                                                                <p>Only instructors can reverse vouchers</p>
                                                            </TooltipContent>
                                                        </Tooltip>
                                                    )}
                                                </>
                                            )}
                                            {v.status === 'reversed' && v.reversal_ref && (
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <span className="text-xs text-purple-600 font-mono">{v.reversal_ref}</span>
                                                    </TooltipTrigger>
                                                    <TooltipContent>
                                                        <p>Reversed by: {v.reversed_by_name || 'N/A'}</p>
                                                        <p>Reason: {v.reversal_reason || 'N/A'}</p>
                                                    </TooltipContent>
                                                </Tooltip>
                                            )}
                                        </div>
                                        </TooltipProvider>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
                </div>
            </div>

            {/* Post Voucher Confirmation Dialog */}
            <Dialog open={!!postTarget} onOpenChange={(open) => !open && closePostDialog()}>
                <DialogContent
                    className="max-w-md"
                    data-testid="post-voucher-confirm-dialog"
                >
                    <DialogHeader>
                        <DialogTitle className="text-emerald-700">Post AP Voucher</DialogTitle>
                        <DialogDescription className="text-sm text-slate-600">
                            Posting will create permanent GL entries for this AP voucher. This action cannot be undone directly; use a reversal if a correction is needed.
                        </DialogDescription>
                    </DialogHeader>
                    {postTarget && (
                        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-2 text-sm">
                            <div className="flex justify-between gap-4">
                                <span className="text-slate-500">Voucher</span>
                                <strong className="font-mono text-slate-900">{postTarget.voucher_number}</strong>
                            </div>
                            <div className="flex justify-between gap-4">
                                <span className="text-slate-500">Supplier</span>
                                <strong className="text-right text-slate-900">{postTarget.supplier_name}</strong>
                            </div>
                            <div className="flex justify-between gap-4">
                                <span className="text-slate-500">Gross</span>
                                <strong className="font-mono text-slate-900">{fmt(postTarget.total_amount || 0)}</strong>
                            </div>
                            {postTarget.withholding_enabled && (
                                <>
                                    <div className="flex justify-between gap-4">
                                        <span className="text-slate-500">WTAX</span>
                                        <strong className="font-mono text-amber-600">{fmt(postTarget.withholding_amount || 0)}</strong>
                                    </div>
                                    <div className="flex justify-between gap-4">
                                        <span className="text-slate-500">Net Payable</span>
                                        <strong className="font-mono text-slate-900">{fmt(postTarget.net_payable || postTarget.total_amount || 0)}</strong>
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                    <DialogFooter className="mt-4">
                        <Button
                            variant="outline"
                            onClick={closePostDialog}
                            disabled={posting}
                            data-testid="cancel-post-voucher-btn"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handlePost}
                            disabled={posting || !postTarget}
                            className="bg-emerald-600 hover:bg-emerald-700"
                            data-testid="confirm-post-voucher-btn"
                        >
                            {posting ? 'Posting...' : 'Post Voucher'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Reversal Confirmation Dialog */}
            <Dialog open={showReversalDialog} onOpenChange={setShowReversalDialog}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="text-purple-700">Reverse AP Voucher</DialogTitle>
                        <DialogDescription className="text-sm text-slate-600">
                            Create reversing GL entries and mark this AP voucher as reversed. This action requires a reason and cannot be undone directly.
                        </DialogDescription>
                    </DialogHeader>
                    {reversalTarget && (
                        <div className="bg-slate-50 rounded-lg p-3 mb-4">
                            <p className="text-sm"><strong>Voucher:</strong> {reversalTarget.voucher_number}</p>
                            <p className="text-sm"><strong>Supplier:</strong> {reversalTarget.supplier_name}</p>
                            <p className="text-sm"><strong>Amount:</strong> {fmt ? fmt(reversalTarget.total_amount) : reversalTarget.total_amount}</p>
                        </div>
                    )}
                    <div className="space-y-2">
                        <Label htmlFor="reversal-reason">Reason for Reversal *</Label>
                        <Textarea
                            id="reversal-reason"
                            placeholder="e.g., Incorrect supplier selected, duplicate entry, etc."
                            value={reversalReason}
                            onChange={(e) => setReversalReason(e.target.value)}
                            rows={3}
                            data-testid="reversal-reason-input"
                        />
                    </div>
                    <DialogFooter className="mt-4">
                        <Button variant="outline" onClick={() => setShowReversalDialog(false)}>Cancel</Button>
                        <Button 
                            onClick={handleReverse} 
                            disabled={reversing || !reversalReason.trim()}
                            className="bg-purple-600 hover:bg-purple-700"
                            data-testid="confirm-reversal-btn"
                        >
                            {reversing ? 'Reversing...' : 'Confirm Reversal'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* RBAC Notice for Students */}
            {!isReadOnly && !perms.can_post && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center gap-3">
                    <Lock className="w-5 h-5 text-amber-600" />
                    <div>
                        <p className="text-sm font-medium text-amber-800">Student Mode</p>
                        <p className="text-xs text-amber-600">You can create and edit draft vouchers. Only instructors can post or cancel vouchers.</p>
                    </div>
                </div>
            )}

            {/* New Voucher Form Dialog */}
            <Dialog open={showForm} onOpenChange={setShowForm}>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>New AP Voucher</DialogTitle>
                        <DialogDescription>
                            Create a draft supplier voucher. Draft AP vouchers stay in Record &gt; AP Voucher and do not affect GL, Trial Balance, AP balances, or WTAX reports until posted.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label>Supplier *</Label>
                                <Select value={formData.supplier_id} onValueChange={(v) => {
                                    const supplier = suppliers.find(s => s.supplier_id === v || s.id === v);
                                    setFormData(prev => ({
                                        ...prev,
                                        supplier_id: v,
                                        withholding_atc: prev.withholding_atc || getMasterAtcSummary(supplier).split(', ')[0] || ''
                                    }));
                                }}>
                                    <SelectTrigger data-testid="supplier-select">
                                        <SelectValue placeholder="Select supplier" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {suppliers.map(s => (
                                            <SelectItem key={s.supplier_id} value={s.supplier_id}>
                                                {s.supplier_id} - {s.supplier_name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Reference # (Supplier Invoice)</Label>
                                <Input
                                    value={formData.reference_number}
                                    onChange={(e) => setFormData(prev => ({ ...prev, reference_number: e.target.value }))}
                                    placeholder="e.g., INV-12345"
                                />
                            </div>
                        </div>
                        <div className="grid grid-cols-3 gap-4">
                            <div>
                                <Label>Voucher Date *</Label>
                                <Input
                                    type="date"
                                    value={formData.voucher_date}
                                    onChange={(e) => setFormData(prev => ({ ...prev, voucher_date: e.target.value }))}
                                />
                            </div>
                            <div>
                                <Label>Due Date</Label>
                                <Input
                                    type="date"
                                    value={formData.due_date}
                                    onChange={(e) => setFormData(prev => ({ ...prev, due_date: e.target.value }))}
                                />
                            </div>
                            <div>
                                <Label>Terms</Label>
                                <Input
                                    value={formData.terms}
                                    onChange={(e) => setFormData(prev => ({ ...prev, terms: e.target.value }))}
                                    placeholder="e.g., Net 30"
                                />
                            </div>
                        </div>
                        <div>
                            <Label>Particulars *</Label>
                            <Textarea
                                value={formData.particulars}
                                onChange={(e) => setFormData(prev => ({ ...prev, particulars: e.target.value }))}
                                placeholder="Description of the bill"
                            />
                        </div>

                        {/* BIR Withholding Foundation */}
                        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-3">
                            <div className="flex items-start justify-between gap-4">
                                <div>
                                    <p className="font-semibold text-slate-800">BIR withholding foundation</p>
                                    <p className="text-xs text-slate-500">Optional expanded withholding capture for this supplier bill.</p>
                                    {selectedSupplier?.has_wtax && (
                                        <p className="text-xs text-emerald-700 mt-1">Supplier profile is marked WTAX. ATC: {selectedSupplierAtc || '-'}</p>
                                    )}
                                </div>
                                <div className="flex items-center gap-2">
                                    <Checkbox
                                        id="withholding-enabled"
                                        checked={!!formData.withholding_enabled}
                                        onCheckedChange={(checked) => updateWithholding({
                                            withholding_enabled: !!checked,
                                            withholding_tax_basis: checked ? (formData.withholding_tax_basis || formData.total_amount) : 0,
                                            withholding_atc: formData.withholding_atc || selectedSupplierAtc.split(', ')[0] || ''
                                        })}
                                    />
                                    <Label htmlFor="withholding-enabled" className="text-sm">Apply WTAX</Label>
                                </div>
                            </div>
                            <div className="grid grid-cols-4 gap-3">
                                <div>
                                    <Label>Tax Basis</Label>
                                    <Input
                                        type="number"
                                        disabled={!formData.withholding_enabled}
                                        value={formData.withholding_tax_basis}
                                        onChange={(e) => updateWithholding({ withholding_tax_basis: parseFloat(e.target.value) || 0 })}
                                    />
                                </div>
                                <div>
                                    <Label>Rate %</Label>
                                    <Input
                                        type="number"
                                        disabled={!formData.withholding_enabled}
                                        value={formData.withholding_rate}
                                        onChange={(e) => updateWithholding({ withholding_rate: parseFloat(e.target.value) || 0 })}
                                    />
                                </div>
                                <div>
                                    <Label>ATC</Label>
                                    <Input
                                        disabled={!formData.withholding_enabled}
                                        value={formData.withholding_atc}
                                        onChange={(e) => setFormData(prev => ({ ...prev, withholding_atc: e.target.value }))}
                                        placeholder="e.g., WC010"
                                    />
                                </div>
                                <div>
                                    <Label>Computed WTAX</Label>
                                    <Input
                                        type="number"
                                        disabled
                                        value={formData.withholding_amount}
                                        className="font-mono"
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label>Liability Account</Label>
                                    <Input
                                        disabled={!formData.withholding_enabled}
                                        value={formData.withholding_account}
                                        onChange={(e) => setFormData(prev => ({ ...prev, withholding_account: e.target.value }))}
                                    />
                                </div>
                                <div className="rounded-md bg-white border border-slate-200 px-3 py-2">
                                    <p className="text-xs text-slate-500">Net payable after WTAX</p>
                                    <p className="font-mono font-semibold text-slate-900">{fmt(netPayablePreview)}</p>
                                </div>
                            </div>
                        </div>

                        {/* Line Items */}
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <Label>Line Items (Expense/Asset Accounts)</Label>
                                <Button variant="outline" size="sm" onClick={handleAddLine}>
                                    <Plus className="w-4 h-4 mr-1" /> Add Line
                                </Button>
                            </div>
                            <div className="border rounded-lg overflow-hidden">
                                <table className="w-full text-sm">
                                    <thead className="bg-slate-50">
                                        <tr>
                                            <th className="text-left px-3 py-2">Account</th>
                                            <th className="text-left px-3 py-2">Description</th>
                                            <th className="text-right px-3 py-2 w-32">Amount</th>
                                            <th className="w-10"></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {formData.lines.map((line, idx) => (
                                            <tr key={idx} className="border-t">
                                                <td className="px-3 py-2">
                                                    <Select
                                                        value={line.account_code}
                                                        onValueChange={(v) => handleLineChange(idx, 'account_code', v)}
                                                    >
                                                        <SelectTrigger className="h-8">
                                                            <SelectValue placeholder="Select account" />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            {accounts.filter(a => ['expense', 'asset'].includes(a.account_type?.toLowerCase())).map(a => (
                                                                <SelectItem key={a.account_code} value={a.account_code}>
                                                                    {a.account_code} - {a.account_name}
                                                                </SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                </td>
                                                <td className="px-3 py-2">
                                                    <Input
                                                        className="h-8"
                                                        value={line.description}
                                                        onChange={(e) => handleLineChange(idx, 'description', e.target.value)}
                                                        placeholder="Description"
                                                    />
                                                </td>
                                                <td className="px-3 py-2">
                                                    <Input
                                                        type="number"
                                                        className="h-8 text-right"
                                                        value={line.amount}
                                                        onChange={(e) => handleLineChange(idx, 'amount', parseFloat(e.target.value) || 0)}
                                                    />
                                                </td>
                                                <td className="px-1">
                                                    {formData.lines.length > 1 && (
                                                        <Button variant="ghost" size="sm" onClick={() => handleRemoveLine(idx)}>
                                                            <Trash2 className="w-4 h-4 text-red-500" />
                                                        </Button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot className="bg-slate-50">
                                        <tr>
                                            <td colSpan="2" className="px-3 py-2 text-right font-semibold">Total:</td>
                                            <td className="px-3 py-2 text-right font-bold text-lg">{fmt(formData.total_amount)}</td>
                                            <td></td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => { setShowForm(false); resetForm(); }}>Cancel</Button>
                        <Button onClick={handleSubmit} className="bg-[#2F8FCB]" data-testid="save-voucher-btn">Save Voucher</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* View Voucher Dialog */}
            <Dialog open={!!selectedVoucher} onOpenChange={() => setSelectedVoucher(null)}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>AP Voucher Details</DialogTitle>
                        <DialogDescription>
                            Review AP voucher source evidence, status, tax snapshot, GL/Trial Balance impact, and next safe action.
                        </DialogDescription>
                    </DialogHeader>
                    {selectedVoucher && (
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4 text-sm">
                                <div><span className="text-slate-500">Voucher #:</span> <strong>{selectedVoucher.voucher_number}</strong></div>
                                <div><span className="text-slate-500">Status:</span> {getStatusBadge(selectedVoucher)}</div>
                                <div><span className="text-slate-500">Supplier:</span> {selectedVoucher.supplier_name}</div>
                                <div><span className="text-slate-500">Reference:</span> {selectedVoucher.reference_number || '-'}</div>
                                <div><span className="text-slate-500">Voucher Date:</span> {selectedVoucher.voucher_date}</div>
                                <div><span className="text-slate-500">Due Date:</span> {selectedVoucher.due_date || '-'}</div>
                            </div>
                            {(selectedVoucher.supplier_tin || selectedVoucher.supplier_is_vatable || selectedVoucher.supplier_has_wtax || hasVoucherWithholding(selectedVoucher) || getAtcSummary(selectedVoucher, 'supplier')) && (
                                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm">
                                    <p className="font-semibold text-slate-700 mb-2">BIR / tax snapshot</p>
                                    <div className="grid grid-cols-2 gap-2">
                                        <div><span className="text-slate-500">TIN:</span> {selectedVoucher.supplier_tin || '-'}</div>
                                        <div><span className="text-slate-500">Registered:</span> {selectedVoucher.supplier_registered_name || selectedVoucher.supplier_name || '-'}</div>
                                        <div><span className="text-slate-500">VATABLE:</span> {selectedVoucher.supplier_is_vatable ? 'Yes' : 'No'}</div>
                                        <div><span className="text-slate-500">Supplier WTAX default:</span> {selectedVoucher.supplier_has_wtax ? 'Yes' : 'No'}</div>
                                        <div><span className="text-slate-500">Voucher WTAX:</span> {hasVoucherWithholding(selectedVoucher) ? 'Yes' : 'No'}</div>
                                        <div className="col-span-2"><span className="text-slate-500">ATC:</span> {getAtcSummary(selectedVoucher, 'supplier') || '-'}</div>
                                    </div>
                                </div>
                            )}
                            {selectedVoucher.withholding_enabled && (
                                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm">
                                    <p className="font-semibold text-amber-800 mb-2">Withholding calculation</p>
                                    <div className="grid grid-cols-2 gap-2">
                                        <div><span className="text-amber-700">Tax Basis:</span> {fmt(selectedVoucher.withholding_tax_basis || 0)}</div>
                                        <div><span className="text-amber-700">Rate:</span> {selectedVoucher.withholding_rate || 0}%</div>
                                        <div><span className="text-amber-700">WTAX:</span> {fmt(selectedVoucher.withholding_amount || 0)}</div>
                                        <div><span className="text-amber-700">ATC:</span> {selectedVoucher.withholding_atc || '-'}</div>
                                        <div className="col-span-2"><span className="text-amber-700">Liability:</span> {selectedVoucher.withholding_account || '-'}</div>
                                    </div>
                                </div>
                            )}
                            <div>
                                <p className="text-slate-500 text-sm">Particulars:</p>
                                <p>{selectedVoucher.particulars}</p>
                            </div>
                            {selectedVoucher.review_status === 'returned' && (
                                <div className="rounded-lg border border-orange-200 bg-orange-50 p-3 text-sm text-orange-900">
                                    <p className="font-semibold">Returned for revision</p>
                                    <p className="mt-1">{selectedVoucher.return_reason || 'Please revise this voucher and resubmit for review.'}</p>
                                    {selectedVoucher.returned_at && (
                                        <p className="mt-1 text-xs text-orange-700">Returned at {new Date(selectedVoucher.returned_at).toLocaleString()}</p>
                                    )}
                                </div>
                            )}
                            <div className="rounded-lg border border-blue-100 bg-blue-50 p-3 text-sm" data-testid="ap-voucher-detail-trail">
                                <p className="font-semibold text-blue-900">AP Voucher evidence trail</p>
                                <p className="mt-1 text-blue-800">
                                    This detail view is read-only evidence. It does not post, reverse, edit, delete, or change GL/Trial Balance records.
                                </p>
                                <div className="mt-3 grid gap-2 md:grid-cols-2">
                                    {(() => {
                                        const trail = getVoucherDetailTrail(selectedVoucher);
                                        return [
                                            ['Where to find it', trail.location],
                                            ['Who reviews next', trail.reviewer],
                                            ['GL / Trial Balance', trail.impact],
                                            ['Next safe action', trail.nextAction],
                                        ].map(([label, value]) => (
                                            <div key={label} className="rounded-md border border-blue-100 bg-white/80 p-2">
                                                <span className="block text-[11px] font-semibold uppercase text-blue-700">{label}</span>
                                                <span className="mt-1 block text-xs text-slate-700">{value}</span>
                                            </div>
                                        ));
                                    })()}
                                </div>
                            </div>
                            <div className="border rounded-lg overflow-hidden">
                                <table className="w-full text-sm">
                                    <thead className="bg-slate-50">
                                        <tr>
                                            <th className="text-left px-3 py-2">Account</th>
                                            <th className="text-left px-3 py-2">Description</th>
                                            <th className="text-right px-3 py-2">Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {selectedVoucher.lines?.map((line, idx) => (
                                            <tr key={idx} className="border-t">
                                                <td className="px-3 py-2">{line.account_name || line.account_code}</td>
                                                <td className="px-3 py-2">{line.description}</td>
                                                <td className="px-3 py-2 text-right font-mono">{fmt(line.amount)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot className="bg-slate-100">
                                        <tr>
                                            <td colSpan="2" className="px-3 py-2 text-right font-semibold">Total:</td>
                                            <td className="px-3 py-2 text-right font-bold">{fmt(selectedVoucher.total_amount)}</td>
                                        </tr>
                                        {selectedVoucher.withholding_enabled && (
                                            <>
                                                <tr>
                                                    <td colSpan="2" className="px-3 py-2 text-right font-semibold">Less WTAX:</td>
                                                    <td className="px-3 py-2 text-right font-bold text-amber-600">{fmt(selectedVoucher.withholding_amount || 0)}</td>
                                                </tr>
                                                <tr>
                                                    <td colSpan="2" className="px-3 py-2 text-right font-semibold">Net Payable:</td>
                                                    <td className="px-3 py-2 text-right font-bold">{fmt(selectedVoucher.net_payable || selectedVoucher.total_amount)}</td>
                                                </tr>
                                            </>
                                        )}
                                        <tr>
                                            <td colSpan="2" className="px-3 py-2 text-right font-semibold">Paid:</td>
                                            <td className="px-3 py-2 text-right font-bold text-emerald-600">{fmt(selectedVoucher.amount_paid)}</td>
                                        </tr>
                                        <tr className="bg-amber-50">
                                            <td colSpan="2" className="px-3 py-2 text-right font-semibold">Balance:</td>
                                            <td className="px-3 py-2 text-right font-bold text-amber-600">{fmt(selectedVoucher.balance)}</td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <TooltipProvider>
                            {perms.can_view_reports ? (
                                <Button 
                                    variant="outline" 
                                    onClick={() => {
                                        const result = generateVoucherPDF(selectedVoucher, fmt);
                                        if (result.success) {
                                            toast.success(`PDF exported: ${result.filename} (${(result.blobSize / 1024).toFixed(2)} KB)`);
                                        } else {
                                            console.error(`[AP Voucher] PDF export failed:`, result.error);
                                            toast.error(`Failed to export PDF: ${result.error}`);
                                        }
                                    }}
                                    data-testid="export-voucher-pdf-btn"
                                >
                                    <Download className="w-4 h-4 mr-2" /> Export PDF
                                </Button>
                            ) : (
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <Button variant="outline" disabled className="text-slate-300">
                                            <Lock className="w-4 h-4 mr-2" /> Export PDF
                                        </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        <p>You don't have permission to export PDFs</p>
                                    </TooltipContent>
                                </Tooltip>
                            )}
                        </TooltipProvider>
                        <Button variant="outline" onClick={() => setSelectedVoucher(null)}>Close</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
